import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { format, startOfDay, subDays, parseISO, isValid } from "date-fns";
import { DateRange } from "@daypicker/react";
import persistReducer from "redux-persist/es/persistReducer";
import storage from "redux-persist/es/storage";

// Filters
const initialStartDate = startOfDay(subDays(new Date(), 30));
const initialEndDate = new Date();
const initialOrderStatus = "LIVE";

interface UserDashboardState {
	startDate: string;
	endDate: string;
	orderStatus: string;
}

export const initialDashboardState: UserDashboardState = {
	startDate: format(initialStartDate, "yyyy-MM-dd"),
	endDate: format(initialEndDate, "yyyy-MM-dd"),
	orderStatus: initialOrderStatus,
};

const getInitialDashboardState = (): UserDashboardState => ({
	startDate: format(startOfDay(subDays(new Date(), 30)), "yyyy-MM-dd"),
	endDate: format(new Date(), "yyyy-MM-dd"),
	orderStatus: initialOrderStatus,
});

const userDashboardFiltersSlice = createSlice({
	name: "userDashboardFilters",
	initialState: initialDashboardState,
	reducers: {
		setDateRange: (state, action: PayloadAction<DateRange>) => {
			const { from, to } = action.payload;

			// Normalize `from` value
			if (from) {
				const parsedFrom = from instanceof Date ? from : parseISO(from as string);
				state.startDate = isValid(parsedFrom) ? format(parsedFrom, "yyyy-MM-dd") : state.startDate;
			}

			// Normalize `to` value
			if (to) {
				const parsedTo = to instanceof Date ? to : parseISO(to as string);
				state.endDate = isValid(parsedTo) ? format(parsedTo, "yyyy-MM-dd") : state.endDate;
			}

			// Debugging logs
			// console.log("Payload received:", action.payload);
			// console.log("Updated startDate:", state.startDate);
			// console.log("Updated endDate:", state.endDate);
		},
		setOrderStatus: (state, action: PayloadAction<string>) => {
			state.orderStatus = action.payload;
		},
		resetFilters: state => {
			state.startDate = format(initialStartDate, "yyyy-MM-dd");
			state.endDate = format(initialEndDate, "yyyy-MM-dd");
			state.orderStatus = initialOrderStatus;
		},
		resetFiltersForLogout: () => getInitialDashboardState(),
	},
});

const configs = {
	key: "dashboard_filters",
	storage,
};

export const dashboardFiltersPersistentReducer = persistReducer(configs, userDashboardFiltersSlice.reducer);
export const { setDateRange, setOrderStatus, resetFilters, resetFiltersForLogout } = userDashboardFiltersSlice.actions;
